/*
PURPOSE: Editorial (base1) rebuild of the public homepage. Composes the full
editorial page (hero → value proposition → how it works → mission → impact →
FAQ) plus the page shell, chrome, and analytics.
USE CASES:
- Serves / (index, redirects authed users to /dashboard via homeLoader) and
  /landing (always public, no redirect).
DOMAIN BOUNDARIES:
- Stays under the App layout subtree (inherits shared Header/Footer/
  AuthModal). Renders its own PublicPageMeta so the homepage ships unique
  title/description/canonical (and the prerender readiness marker) rather
  than App.tsx's generic defaults.
- Every section reads copy verbatim from useCopy()/publicPages; no fabricated
  copy ships. Coral visualisations are the bloom wrappers.
*/

import React, { useEffect, useRef } from 'react';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import Hero from './Hero';
import { WhyCoralSection } from '../../../components/editorial';
import HowItWorks from './HowItWorks';
import Mission from './Mission';
import Impact from './Impact';
import Faq from './Faq';
import PublicPageErrorBoundary from '../../../components/PublicPageErrorBoundary/PublicPageErrorBoundary';
import PublicPageMeta from '../../../components/MetaHead/PublicPageMeta';

const Home: React.FC = () => {
  const coralCreatorRef = useRef<HTMLDivElement>(null);

  // Fire the homepage view event on mount; deferred to the next tick so
  // first render isn't blocked by analytics.
  useEffect(() => {
    setTimeout(() => {
      trackEvent(EventName.HOME_PAGE_VIEW, {});
    }, 0);
  }, []);

  // Scroll to the hero creator — section CTAs land on the same target.
  const scrollToCoralCreator = () => {
    if (coralCreatorRef.current) {
      setTimeout(() => {
        coralCreatorRef.current!.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  };

  return (
    <PublicPageErrorBoundary>
      <div className="flex flex-col min-h-screen bg-ivory">
        <PublicPageMeta
          title="Back the music that moves you | wearecoral"
          description="Back artists you choose with one monthly contribution, split your way. Join a community of people who care enough to back music's future."
          path="/"
        />
        <Hero creatorRef={coralCreatorRef} />

        <WhyCoralSection />

        <HowItWorks onPersonalise={scrollToCoralCreator} />

        <Mission onAddToEcosystem={scrollToCoralCreator} />

        <Impact onPersonalise={scrollToCoralCreator} />

        <Faq />
      </div>
    </PublicPageErrorBoundary>
  );
};

export default Home;
